import { DomainModelRole } from '@/constants/cardTypes'
import { functionType } from '@/composables/useWorkflowAssistantFunctions'
import { resolveSchema, resolveDomainEvent, resolveLane } from '@/shared/utils/workflowSpec'
import { sanitizeCardDescription } from '@/shared/utils/sanitize'

/**
 * Main validation function
 * @param {string} functionName - The name of the function to execute
 * @param {object} args - The arguments passed to the function
 * @param {object} context - Additional context (workflow, svgGraph, etc.)
 * @returns {object} - { valid: boolean, error: string | null }
 */
export const validateWorkflowAssistantAction = (functionName, args, context) => {
  const validators = {
    [functionType.CREATE_CARD]: validateCreateCard,
    [functionType.CREATE_EVENT]: validateCreateEvent,
    [functionType.DELETE_EVENT]: validateDeleteEvent,
    [functionType.DELETE_LANE]: validateDeleteLane,
    [functionType.DELETE_ENTITY]: validateDeleteEntity,
    [functionType.UPDATE_ENTITY]: validateUpdateEntity,
    [functionType.UPDATE_CARD]: validateUpdateCard,
    [functionType.CREATE_COMMAND]: validateCreateCommand,
    [functionType.CREATE_READ_MODEL]: validateCreateReadModel,
    [functionType.UPDATE_READ_MODEL]: validateUpdateReadModel
  }

  const validator = validators[functionName]

  if (!validator) {
    // No specific validation needed for this function
    return { valid: true, error: null }
  }

  return validator(args, context)
}

/**
 * Validates creating a card
 * Prevents adding multiple command or aggregate root cards to the same event
 */
const validateCreateCard = (args, context) => {
  const { domainEvent: eventRef, cardTypeId } = args
  const { workflow, cardTypes } = context

  if (!eventRef || !cardTypeId) {
    return {
      valid: false,
      error: 'Missing required information: domain event or card type ID.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const cardType = (cardTypes || []).find((ct) => ct.id === cardTypeId)

  if (!cardType) {
    return {
      valid: false,
      error: 'Card type not found. Please specify a valid card type.'
    }
  }

  const isCommandOrAggregate =
    [DomainModelRole.COMMAND, DomainModelRole.AGGREGATE_ROOT].includes(cardType.domainModelRole)

  if (!isCommandOrAggregate) {
    return { valid: true, error: null }
  }

  const existingCards = event.cards || []

  const sameRoleCardTypeIds = (cardTypes || [])
    .filter((ct) => ct.domainModelRole === cardType.domainModelRole)
    .map((ct) => ct.id)

  const hasExistingCardOfSameRole = existingCards.some((card) =>
    sameRoleCardTypeIds.includes(card.cardTypeId)
  )

  if (hasExistingCardOfSameRole) {
    const cardTypeName = cardType.domainModelRole === DomainModelRole.COMMAND ? 'Command' : 'Aggregate Root'
    return {
      valid: false,
      error: `Cannot add ${cardTypeName} card. The event "${event.description}" already has a ${cardTypeName} card. Each event can only have one ${cardTypeName} card.`
    }
  }

  return { valid: true, error: null }
}

const validateUpdateCard = (args, context) => {
  const { domainEvent: eventRef, currentDescription, schema: schemaRef } = args
  const { workflow, cardTypes } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event is required.'
    }
  }

  if (!currentDescription) {
    return {
      valid: false,
      error: 'Current card description is required to identify the card.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const sanitized = sanitizeCardDescription(currentDescription)
  const card = (event.cards || []).find((c) =>
    sanitizeCardDescription(c.description || '') === sanitized
  )

  if (!card) {
    return {
      valid: false,
      error: `Card "${currentDescription}" not found on event "${event.description}".`
    }
  }

  if (schemaRef !== undefined && schemaRef) {
    const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))
    const cardType = cardTypesMap[card.cardTypeId]

    const allowedRoles = [
      DomainModelRole.COMMAND,
      DomainModelRole.AGGREGATE_ROOT,
      DomainModelRole.READ_MODEL
    ]

    if (cardType && !allowedRoles.includes(cardType.domainModelRole)) {
      return {
        valid: false,
        error: `Cannot assign schema to this card. Schema can only be assigned to cards with domain model role: Command, AggregateRoot, or ReadModel. This card has role: ${cardType.domainModelRole || 'none'}.`
      }
    }

    const schema = resolveSchema(schemaRef, workflow, cardTypesMap)

    if (!schema) {
      return {
        valid: false,
        error: `Schema "${refDisplayName(schemaRef)}" not found.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating an event
 */
const validateCreateEvent = (args, context) => {
  const { description, laneName, parentEvent: parentEventRef } = args
  const { workflow } = context

  if (!description || description.trim() === '') {
    return {
      valid: false,
      error: 'Event description is required. Please provide a name for the event.'
    }
  }

  if (description.length > 40) {
    return {
      valid: false,
      error: `Event description is too long (${description.length} characters). Maximum length is 40 characters. Please use a shorter description.`
    }
  }

  // Check if parent event exists (if specified and not 'start')
  if (parentEventRef && parentEventRef !== 'start') {
    const parentEvent = resolveDomainEvent(parentEventRef, workflow)

    if (!parentEvent) {
      return {
        valid: false,
        error: `Parent event "${refDisplayName(parentEventRef)}" not found. Please specify an existing event or use "start" to create a new starting point.`
      }
    }
  }

  // Check if lane exists (if specified)
  if (laneName && !resolveLane(laneName, workflow)) {
    return {
      valid: false,
      error: `Lane "${laneName}" not found. Available lanes: ${(workflow.lanes || []).map((l) => `"${l.name}"`).join(', ')}. You may need to create the lane first.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting an event
 */
const validateDeleteEvent = (args, context) => {
  const { event: eventRef } = args
  const { workflow, svgGraph } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Event reference is required to delete an event.'
    }
  }

  const resolvedEvent = resolveDomainEvent(eventRef, workflow)

  if (!resolvedEvent) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const eventId = resolvedEvent.id
  const svgNode = svgGraph[eventId]

  if (!svgNode) {
    return {
      valid: false,
      error: 'Event not found in the diagram.'
    }
  }

  // Check if this is a decision with multiple children
  if (svgNode.type === 'bpmn:Gateway') {
    const children = Object.values(svgGraph).filter((node) =>
      node.parents && node.parents.includes(eventId)
    )

    if (children.length > 1) {
      return {
        valid: false,
        error: `Cannot delete decision "${svgNode.name}". This decision has multiple child events (${children.length}). Please reduce the number of children to one before deleting, or delete the child events first.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting a lane
 */
const validateDeleteLane = (args, context) => {
  const { lane: laneName } = args
  const { workflow, svgGraph } = context

  if (!laneName) {
    return {
      valid: false,
      error: 'Lane name is required to delete a lane.'
    }
  }

  const resolvedLane = resolveLane(laneName, workflow)

  if (!resolvedLane) {
    return {
      valid: false,
      error: `Lane "${laneName}" not found. Available lanes: ${(workflow.lanes || []).map((l) => `"${l.name}"`).join(', ')}.`
    }
  }

  // Check if lane has any events
  const hasEvents = Object.values(svgGraph).some((node) => node.laneId === resolvedLane.id)

  if (hasEvents) {
    return {
      valid: false,
      error: `Cannot delete lane "${laneName}". This lane contains events. Please move or delete all events from this lane before deleting it.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting an entity
 */
const validateDeleteEntity = (args, context) => {
  const { entity: entityRef } = args
  const { workflow } = context

  if (!entityRef) {
    return {
      valid: false,
      error: 'Entity reference is required to delete an entity.'
    }
  }

  const entity = resolveSchema(entityRef, workflow)

  if (!entity) {
    return {
      valid: false,
      error: `Entity "${refDisplayName(entityRef)}" not found.`
    }
  }

  // Check if entity is referenced by other entities
  const entityId = entity.id
  const referencedBy = [];

  (workflow.schemas || []).forEach((otherEntity) => {
    if (otherEntity.id === entityId) {
      return
    }

    const hasReference = (otherEntity.fields || []).some((field) => field.relatedEntityId === entityId)

    if (hasReference) {
      referencedBy.push(otherEntity.name)
    }
  })

  if (referencedBy.length > 0) {
    return {
      valid: false,
      error: `Cannot delete entity "${entity.name}". It is referenced by other entities: ${referencedBy.join(', ')}. Please remove these references first.`
    }
  }

  // Check if entity is used by any AggregateRoot cards (schemaId points to Entity)
  const usedByEvents = [];

  (workflow.domainEvents || []).forEach((event) => {
    const hasEntityInAggregateCards = (event.cards || []).some((card) => {
      // AggregateRoot cards have schemaId pointing directly to an Entity
      if (card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
        return card.schemaId === entityId
      }

      return false
    })

    if (hasEntityInAggregateCards) {
      usedByEvents.push(event.description)
    }

    // Check if any ReadModel cards use Query schemas that reference this entity
    const hasEntityInQuerySchemas = (event.cards || []).some((card) => {
      if (card.cardType?.domainModelRole !== DomainModelRole.READ_MODEL || !card.schemaId) {
        return false
      }
      // Query schemas have entityId pointing to the Entity they query
      const querySchema = (workflow.schemas || []).find((s) => s.id === card.schemaId)

      return querySchema?.entityId === entityId
    })

    if (hasEntityInQuerySchemas) {
      usedByEvents.push(event.description)
    }

    // Check if schemas linked to cards have fields referencing this entity
    const hasEntityInSchemaFields = (event.cards || []).some((card) => {
      if (!card.schemaId) {
        return false
      }
      const schema = (workflow.schemas || []).find((s) => s.id === card.schemaId)

      return (schema?.fields || []).some((field) => field.relatedEntityId === entityId)
    })

    if (hasEntityInSchemaFields) {
      usedByEvents.push(event.description)
    }
  })

  if (usedByEvents.length > 0) {
    return {
      valid: false,
      error: `Cannot delete entity "${entity.name}". It is used by events: ${[...new Set(usedByEvents)].join(', ')}. Please remove these references first.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating an entity
 */
const validateUpdateEntity = (args, context) => {
  const { entity: entityRef, name, addFields, updateFields, removeFields } = args
  const { workflow } = context

  if (!entityRef) {
    return {
      valid: false,
      error: 'Entity reference is required to update an entity.'
    }
  }

  const entity = resolveSchema(entityRef, workflow)

  if (!entity) {
    return {
      valid: false,
      error: `Entity "${refDisplayName(entityRef)}" not found.`
    }
  }

  if (name && name.trim() === '') {
    return {
      valid: false,
      error: 'Entity name cannot be empty.'
    }
  }

  // Validate addFields
  if (addFields && Array.isArray(addFields)) {
    for (const field of addFields) {
      if (!field.name || field.name.trim() === '') {
        return {
          valid: false,
          error: 'All new fields must have a name.'
        }
      }

      if (field.relatedEntity) {
        const relatedEntity = resolveSchema(field.relatedEntity, workflow)

        if (!relatedEntity) {
          return {
            valid: false,
            error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
          }
        }
      }
    }
  }

  // Validate updateFields
  if (updateFields && Array.isArray(updateFields)) {
    for (const { fieldName, updates } of updateFields) {
      if (!fieldName) {
        return {
          valid: false,
          error: 'Field name is required for updating a field.'
        }
      }

      if (updates?.relatedEntity) {
        const relatedEntity = resolveSchema(updates.relatedEntity, workflow)

        if (!relatedEntity) {
          return {
            valid: false,
            error: `Related entity "${refDisplayName(updates.relatedEntity)}" not found for field "${fieldName}".`
          }
        }
      }
    }
  }

  // Validate removeFields
  if (removeFields && Array.isArray(removeFields)) {
    for (const fieldName of removeFields) {
      if (!fieldName || fieldName.trim() === '') {
        return {
          valid: false,
          error: 'Field name is required for removing a field.'
        }
      }
    }
  }

  return { valid: true, error: null }
}

const validateCreateCommand = (args, context) => {
  const { domainEvent: eventRef, name, fields } = args
  const { workflow } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event is required to create command.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  if (!name || name.trim() === '') {
    return {
      valid: false,
      error: 'Command name is required.'
    }
  }

  if (fields && Array.isArray(fields)) {
    for (const field of fields) {
      if (!field.name || field.name.trim() === '') {
        return {
          valid: false,
          error: 'All command fields must have a name.'
        }
      }

      if (field.relatedEntity) {
        const relatedEntity = resolveSchema(field.relatedEntity, workflow)

        if (!relatedEntity) {
          return {
            valid: false,
            error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

const validateCreateReadModel = (args, context) => {
  const { domainEvent: eventRef, name, fields, entity: entityRef } = args
  const { workflow } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event is required to create read model.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  if (!name || name.trim() === '') {
    return {
      valid: false,
      error: 'Read model name is required.'
    }
  }

  if (fields && Array.isArray(fields)) {
    for (const field of fields) {
      if (!field.name || field.name.trim() === '') {
        return {
          valid: false,
          error: 'All read model fields must have a name.'
        }
      }

      if (field.relatedEntity) {
        const relatedEntity = resolveSchema(field.relatedEntity, workflow)

        if (!relatedEntity) {
          return {
            valid: false,
            error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
          }
        }
      }
    }
  }

  if (entityRef) {
    const entity = resolveSchema(entityRef, workflow)

    if (!entity) {
      return {
        valid: false,
        error: `Entity "${refDisplayName(entityRef)}" not found.`
      }
    }
  }

  return { valid: true, error: null }
}

const validateUpdateReadModel = (args, context) => {
  const { readModel: readModelRef, entity: entityRef, addFields, updateFields, removeFields } = args
  const { workflow } = context

  if (!readModelRef) {
    return {
      valid: false,
      error: 'Read model reference is required.'
    }
  }

  if (entityRef) {
    const entity = resolveSchema(entityRef, workflow)

    if (!entity) {
      return {
        valid: false,
        error: `Entity "${refDisplayName(entityRef)}" not found.`
      }
    }
  }

  if (addFields && Array.isArray(addFields)) {
    for (const field of addFields) {
      if (!field.name || field.name.trim() === '') {
        return {
          valid: false,
          error: 'All new fields must have a name.'
        }
      }
    }
  }

  if (updateFields && Array.isArray(updateFields)) {
    for (const { fieldName } of updateFields) {
      if (!fieldName) {
        return {
          valid: false,
          error: 'Field name is required for updating a field.'
        }
      }
    }
  }

  if (removeFields && Array.isArray(removeFields)) {
    for (const fieldName of removeFields) {
      if (!fieldName || fieldName.trim() === '') {
        return {
          valid: false,
          error: 'Field name is required for removing a field.'
        }
      }
    }
  }

  return { valid: true, error: null }
}

const refDisplayName = (ref) => {
  if (!ref || typeof ref !== 'string') {
    return ref
  }
  const lastSegment = ref.split('/').pop()

  return lastSegment.replace(/([a-z])([A-Z])/g, '$1 $2')
}
